import { FC, useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { useOktaAuth } from '@okta/okta-react'

import { Button, Form, SpaceBetween } from '@cloudscape-design/components'

import LoadingSpinner from '../../LoadingSpinner'
import AccountNetworkApprovalPanel from '../AccountNetworkApprovalPanel'
import {
	accountRecordShowsIpAllocationAzC,
	vpcAvailabilityZonesShowAzC
} from '../../../helper/accountRequestNetworkSummary'
import { buildNetworkApprovalFormValues } from '../../../helper/accountNetworkApprovalPrefill'
import {
	buildAccountNetworkDetailsPayload,
	validateAccountNetworkDetails
} from '../../../helper/accountNetworkValidation'

import { useApprovalFormStore } from '../../../stores/accounts/approvalFormStore'
import useSingleAccount from '../../../hooks/query/useSingleAccount'
import useAccounts from '../../../hooks/query/useAccounts'
import useAcceptAccount from '../../../hooks/mutate/useAcceptAccount'
import type { AccountApprovalRequest } from '../../../types/account'
import useApproveTags from '../../../hooks/mutate/useApproveTags'
import { ApproveTags } from '../../../types/tags'

interface ApprovalFormProps {
	thisAccountRequestID: string
	shouldShowApprovalForm: (shouldShow: boolean) => void
}

type PlatformSubgroupValidation = {
	error?: string
	info?: string
}

const ApprovalForm: FC<ApprovalFormProps> = ({
	thisAccountRequestID,
	shouldShowApprovalForm
}) => {
	const { authState } = useOktaAuth()
	const thisUserEmail = authState?.idToken?.claims.email as string

	const {
		formValues,
		actions: { setFormValues, clearFormValues, replaceFormValues }
	} = useApprovalFormStore()

	const { data: thisAccount, isLoading: singleAccountDataLoading } =
		useSingleAccount(thisAccountRequestID, true)

	const { mutate: approveTagsById } = useApproveTags(thisAccount?.tagsID || '')
	const { mutate: acceptAccount } = useAcceptAccount(thisAccountRequestID)
	const { data: accounts } = useAccounts()

	const [isNetworkValid, setIsNetworkValid] = useState(false)
	const [networkPrefillGeneration, setNetworkPrefillGeneration] = useState(0)

	const detailsFingerprint = useMemo(() => {
		if (!thisAccount?.details) return ''
		const d = thisAccount.details as Record<string, unknown>
		const systemAlias = thisAccount.tags?.details?.systemAlias
		return JSON.stringify({
			details: d,
			accountType: thisAccount.type,
			systemAlias: systemAlias !== undefined ? systemAlias : undefined
		})
	}, [thisAccount?.details, thisAccount?.type, thisAccount?.tags?.details?.systemAlias])

	useLayoutEffect(() => {
		if (singleAccountDataLoading || !thisAccount?.details) return
		const d = thisAccount.details as Record<string, unknown>
		const systemAlias = thisAccount.tags?.details?.systemAlias
		const distributionList = String(
			d.distributionList ?? thisAccount.accountRequestID ?? ''
		).trim()
		clearFormValues()
		replaceFormValues(
			buildNetworkApprovalFormValues(d, thisAccount.type, {
				systemAlias,
				distributionList
			})
		)
		setNetworkPrefillGeneration((generation) => generation + 1)
		// eslint-disable-next-line react-hooks/exhaustive-deps -- sync when fingerprint changes
	}, [thisAccountRequestID, singleAccountDataLoading, detailsFingerprint, clearFormValues, replaceFormValues])

	useEffect(() => {
		return () => clearFormValues()
	}, [clearFormValues])

	const systemAlias = thisAccount?.tags?.details?.systemAlias ?? ''

	const platformSubgroupValidation = useMemo<
		PlatformSubgroupValidation | undefined
	>(() => {
		if (!systemAlias || !accounts) return undefined
		const normalizedAlias = systemAlias.trim().toLowerCase()
		const currentType = thisAccount?.type

		const matchingAccounts = accounts.filter((account) => {
			if (account.accountRequestID === thisAccountRequestID) return false
			const existing = account.approval?.platformSubgroupName
			return existing?.trim().toLowerCase() === normalizedAlias
		})

		if (matchingAccounts.length === 0) return undefined

		const sameTypeConflict = matchingAccounts.find(
			(account) => account.type === currentType
		)
		if (sameTypeConflict) {
			return {
				error: `This Platform Subgroup Name is already in use by another approved account request (type: ${sameTypeConflict.type}).`
			}
		}

		const otherTypes = Array.from(
			new Set(
				matchingAccounts
					.map((account) => account.type)
					.filter((type): type is string => Boolean(type && type !== currentType))
			)
		)

		if (otherTypes.length > 0) {
			return {
				info: `This Platform Subgroup Name already exists for ${otherTypes.join(
					', '
				)} account type(s). Reuse across different account types is allowed.`
			}
		}

		return undefined
	}, [systemAlias, accounts, thisAccountRequestID, thisAccount])

	const hasDuplicateError = Boolean(platformSubgroupValidation?.error)
	// Production onboarding defaults to three zones but may drop the third; non-prod and
	// sandbox stay fixed at two, so only Production gets the toggle.
	const allowThirdAzToggle = accountRecordShowsIpAllocationAzC(thisAccount?.type)
	const isProduction = vpcAvailabilityZonesShowAzC(
		formValues.vpcAvailabilityZones,
		thisAccount?.type
	)

	if (!thisAccount || singleAccountDataLoading) return <LoadingSpinner />

	const handleApprove = () => {
		if (hasDuplicateError || !isNetworkValid) return

		if (!validateAccountNetworkDetails(formValues, isProduction)) {
			return
		}

		const networkPayload = buildAccountNetworkDetailsPayload(formValues, isProduction)
		const approvalDetails = {
			...JSON.parse(JSON.stringify(formValues)),
			...networkPayload,
			platformSubgroupName: systemAlias
		} as Record<string, unknown>

		const raw = thisAccount.details.SSOUserNames
		if (Array.isArray(raw)) {
			approvalDetails.SSOUserNames = raw.map(
				(user: { displayName?: string; userName: string }) => ({
					userName: user.userName ?? '',
					displayName: (user.displayName ?? '').trim()
				})
			)
		} else if (typeof raw === 'string' && raw.trim()) {
			approvalDetails.SSOUserNames = raw.split(',').map((s) => ({
				userName: s.trim(),
				displayName: ''
			}))
		} else {
			approvalDetails.SSOUserNames = []
		}

		const approvalRequest: AccountApprovalRequest = {
			accountRequestID: thisAccountRequestID,
			action: 'APPROVE',
			approverID: thisUserEmail,
			details: approvalDetails
		}

		const tagRequest: ApproveTags = {
			action: 'UPDATE_APPROVE_STATUS',
			status: 'APPROVED',
			tagsID: thisAccount?.tagsID || ''
		}

		approveTagsById(tagRequest, {
			onSuccess: () => {
				acceptAccount(approvalRequest, {
					onSuccess: () => {
						shouldShowApprovalForm(false)
						clearFormValues()
					}
				})
			}
		})
	}

	const handleCancel = () => {
		clearFormValues()
		shouldShowApprovalForm(false)
	}

	return (
		<Form
			actions={
				<SpaceBetween direction="horizontal" size="xs">
					<Button variant="link" onClick={handleCancel}>
						Cancel
					</Button>
					<Button
						variant="primary"
						disabled={hasDuplicateError || !isNetworkValid}
						onClick={handleApprove}
					>
						Approve
					</Button>
				</SpaceBetween>
			}
		>
			<AccountNetworkApprovalPanel
				formValues={formValues}
				setFormValues={setFormValues}
				isProduction={isProduction}
				accountType={thisAccount.type}
				onValidityChange={setIsNetworkValid}
				platformSubgroupValidation={platformSubgroupValidation}
				networkPrefillGeneration={networkPrefillGeneration}
				allowThirdAzToggle={allowThirdAzToggle}
			/>
		</Form>
	)
}

export default ApprovalForm
